import React from 'react';
import { Button } from 'reactstrap';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx-js-style';

const ExportXLSX = ({ csvData, fileName, title }) => {
    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';

    // Utility function to convert headers to Camel Case (First Letter Capital)
    const toCamelCase = (str) => {
        return str
            .replace(/([a-z])([A-Z])/g, "$1 $2") // Split camel case words
            .replace(/[_-]/g, " ") // Replace underscores or hyphens with spaces
            .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word
    };

    const exportToCSV = (csvData, fileName) => {
        // Prepare the title row and the row with the date and username
        const titleRow = [[`${title || "Data Report"} - ${localStorage.getItem("clientName") || "Unknown"}`]];
        const today = new Date().toLocaleDateString();
        const generatedByRow = [[`Date: ${today}, Generated by: ${localStorage.getItem("fullName") || "Unknown"}`]];

        // Extract the headers from csvData and convert to Camel Case
        const headers = [Object.keys(csvData[0]).map(key => toCamelCase(key))];

        // Convert the data rows to array format
        const dataRows = csvData.map(row => Object.values(row));

        // Create a worksheet with an empty row first
        const ws = XLSX.utils.aoa_to_sheet([[]]);

        // Add the title row
        XLSX.utils.sheet_add_aoa(ws, titleRow, { origin: "A2" });

        // Add the generated by and date row
        XLSX.utils.sheet_add_aoa(ws, generatedByRow, { origin: "A3" });

        // Add the headers at position A4
        XLSX.utils.sheet_add_aoa(ws, headers, { origin: "A4" });

        // Add the data rows starting at A5
        XLSX.utils.sheet_add_aoa(ws, dataRows, { origin: "A5" });

        const colCount = headers[0].length; // Number of columns based on headers
        ws['!merges'] = [
            { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } }, // Merge title row
            { s: { r: 2, c: 0 }, e: { r: 2, c: colCount - 1 } }  // Merge date and generated by row
        ];

        // Apply the thick border and style for the title row (row 2)
        for (let i = 0; i < colCount; i++) {
            const cellAddress = XLSX.utils.encode_cell({ r: 1, c: i });
            ws[cellAddress] = {
                v: i === 0 ? titleRow[0][0] : "",
                s: {
                    font: { bold: true, sz: 16 },
                    alignment: { horizontal: 'center' },
                    border: {
                        top: { style: 'thick', color: { rgb: '000000' } },
                        bottom: { style: 'thick', color: { rgb: '000000' } },
                        left: { style: i === 0 ? 'thick' : 'thin', color: { rgb: '000000' } },
                        right: { style: i === colCount - 1 ? 'thick' : 'thin', color: { rgb: '000000' } }
                    }
                }
            };
        }

        // Apply style for the date and generated by row (row 3)
        for (let i = 0; i < colCount; i++) {
            const cellAddress = XLSX.utils.encode_cell({ r: 2, c: i });
            ws[cellAddress] = {
                v: i === 0 ? generatedByRow[0][0] : "",
                s: {
                    font: { italic: true, sz: 12 },
                    alignment: { horizontal: 'center' },
                    border: {
                        top: { style: 'thin', color: { rgb: '000000' } },
                        bottom: { style: 'thin', color: { rgb: '000000' } },
                        left: { style: i === 0 ? 'thick' : 'thin', color: { rgb: '000000' } },
                        right: { style: i === colCount - 1 ? 'thick' : 'thin', color: { rgb: '000000' } }
                    }
                }
            };
        }

        // Set the height for the empty row (A1), title row (A2), and date row (A3)
        if (!ws['!rows']) ws['!rows'] = [];
        ws['!rows'][0] = { hpt: 2 }; // Empty row height
        ws['!rows'][1] = { hpt: 30 }; // Title row height
        ws['!rows'][2] = { hpt: 20 }; // Date row height

        // Set styles for the header row (A4)
        for (let i = 0; i < colCount; i++) {
            const cellAddress = XLSX.utils.encode_cell({ r: 3, c: i });
            ws[cellAddress] = {
                v: headers[0][i],
                s: {
                    font: { bold: true },
                    fill: { fgColor: { rgb: "28a745" } }, // Set background color
                    border: {
                        top: { style: 'thin', color: { rgb: '000000' } },
                        bottom: { style: 'thin', color: { rgb: '000000' } },
                        left: { style: 'thin', color: { rgb: '000000' } },
                        right: { style: 'thin', color: { rgb: '000000' } }
                    }
                }
            };
        }

        // Set borders for the data rows
        for (let i = 0; i < dataRows.length; i++) {
            for (let j = 0; j < headers[0].length; j++) {
                const cellAddress = XLSX.utils.encode_cell({ r: i + 4, c: j }); // Data starts from A5
                ws[cellAddress] = {
                    v: dataRows[i][j],
                    s: {
                        border: {
                            top: { style: 'thin', color: { rgb: '000000' } },
                            bottom: { style: 'thin', color: { rgb: '000000' } },
                            left: { style: 'thin', color: { rgb: '000000' } },
                            right: { style: 'thin', color: { rgb: '000000' } }
                        }
                    }
                };
            }
        }

        // Set column widths
        ws['!cols'] = [
            { wch: 25 },
            { wch: 15 },
            { wch: 15 },
            { wch: 20 },
            { wch: 20 },
            { wch: 20 },
            { wch: 20 }
        ];

        // Create a workbook and export it
        const wb = { Sheets: { 'data': ws }, SheetNames: ['data'] };
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: fileType });
        FileSaver.saveAs(data, fileName + fileExtension);
    };

    return (
        <Button outline size="sm" onClick={() => exportToCSV(csvData, fileName)}>Download XLSX</Button>
    );
};

export default ExportXLSX;
